/**
 * ONE spelling of "the primitive drives its own state off this event, AND the caller still gets it".
 *
 * IT EXISTS BECAUSE THE HAND-ROLLED FORM IS A SILENT DELETION. A primitive that writes `onMouseEnter={() =>
 * setIsHovered(true)}` beside a `{...props}` spread has two ways to lose: written BEFORE the spread, a caller's own
 * handler wins and the primitive's state never updates; written AFTER it, the caller's handler is dropped and they
 * never learn. Both were live in `components/primitives` -- `Button` composed its focus handler and then let a trailing
 * spread overwrite it, and `Card` wrote its hover pair ahead of one. The cure at each site is to pull the caller's
 * handler OUT of the spread by destructuring it and compose it here, so the JSX ordering stops deciding anything.
 *
 * THE PRIMITIVE'S OWN WORK RUNS FIRST, always, and that order is the contract rather than an accident: a caller's
 * handler may call `stopPropagation`, re-render, or unmount, and the state update the primitive owes its own rendering
 * must not be the thing that gets skipped.
 */

/** Compose the primitive's own handler with the caller's, if the caller supplied one. */
export function composeHandler<E>(own: (event: E) => void, caller: ((event: E) => void) | undefined): (event: E) => void {
	return (event: E) => {
		own(event);
		caller?.(event);
	};
}

/**
 * The flag arm every state-tracking primitive shares: set its OWN boolean flag -- hovered, pressed, focused -- to
 * `flag`, then hand the caller the event. Each primitive's hover pair, press pair and blur arm is a call of this.
 */
export function setFlagThen<E>(
	setFlag: (flag: boolean) => void,
	flag: boolean,
	caller: ((event: E) => void) | undefined,
): (event: E) => void {
	return composeHandler(() => setFlag(flag), caller);
}
